import { type ReactNode } from "react";
import ToolTip from "shared/components/common/ToolTip";
import InfoIcon from "shared/components/icons/InfoIcon";

export type PillarTableFilterToolTipProps = {
  /**
   * Rendered inside the hover tooltip that sits after the filter's title.
   * Omit it and no icon is rendered at all.
   */
  toolTip?: ReactNode;
  filterKey: string;
};

/**
 * The info icon + hover tooltip that every filter renders after its title.
 * Exported on its own for the filters that have no `<label>` to hang it off of.
 */
export const PillarTableFilterToolTip = ({
  toolTip,
  filterKey,
}: PillarTableFilterToolTipProps) => {
  if (!toolTip) {
    return null;
  }
  return (
    <ToolTip
      className="cursor-pointer flex items-center"
      tooltipClassName="mt-0.25 py-1 px-2 bg-neutral-light text-xs rounded-md border border-neutral-mid-300"
      placement="bottom-start"
      tooltip={
        <div
          data-testid={`filter-tooltip-content-${filterKey}`}
          className="flex flex-col text-sm"
        >
          {toolTip}
        </div>
      }
    >
      <InfoIcon
        data-testid={`filter-tooltip-${filterKey}`}
        className="text-status-general-tint h-2.5 w-2.5"
      />
    </ToolTip>
  );
};

export type PillarTableFilterLabelProps = PillarTableFilterToolTipProps & {
  title: string;
  htmlFor?: string;
  className?: string;
};

/**
 * A filter's title, optionally followed by an info tooltip. `className` is the
 * label's own class so each filter keeps the typography it already had.
 */
const PillarTableFilterLabel = ({
  title,
  toolTip,
  filterKey,
  htmlFor,
  className,
}: PillarTableFilterLabelProps) => (
  <div className="flex flex-row items-center gap-x-0.25">
    <label className={className} htmlFor={htmlFor}>
      {title}
    </label>
    <PillarTableFilterToolTip toolTip={toolTip} filterKey={filterKey} />
  </div>
);

export default PillarTableFilterLabel;
